import * as $ from 'jquery';
import { CONFIG } from './config';
import { X_SEMIOTY_TENANT } from './constants';
import { CUSTOM_MOBILE, CUSTOM_SCRIPTS, MODULES_SUMMARY, TENANTS } from './endpoints';

declare var filtersAndComponentsExports: any;
declare var mobileExports: any;

type TenantInfo = { name: string };

function isMobile(): boolean {
    return window.location.href.indexOf('mobile=true') > -1 || window.localStorage.getItem('mobile') == 'true';
}

function isLocal(): boolean {
    return window.location.protocol == 'http:';
}

export function isOnline(): boolean {
    return navigator.onLine;
}

function getTenantNameFromHostname(): string {
    if (HOSTNAME) {
        var pos = HOSTNAME.indexOf('.')
        if (pos > -1) {
            return HOSTNAME.substring(0, pos);
        }
    }
    return HOSTNAME;
}

function getServitlyTenantInfo(): Promise<TenantInfo> {
    return new Promise(function (resolve) {
        if (!isLocal() && isOnline()) {
            let newBaseUrl = getNewBaseUrl();
            $.get(newBaseUrl + TENANTS, { domainAlias: HOSTNAME, skipInterceptor: true }, (data) => {
                if (data.name) {
                    API_URL = newBaseUrl; // set new base url
                    resolve(data);
                } else {
                    getTenantWithBaseUrl(resolve);
                }
            }).fail(function () {
                getTenantWithBaseUrl(resolve);
            });
        } else { // local or offline
            getTenantWithBaseUrl(resolve);
        }
    });
}

function getNewBaseUrl() {
    let baseUrlParts = CONFIG.BASE_URL.split(':');
    let port = baseUrlParts.length > 2 ? (':' + baseUrlParts[2]) : '';
    let newBaseUrl = baseUrlParts[0] + '://' + HOSTNAME + port + '/api';
    return newBaseUrl;
}

function getTenantWithBaseUrl(resolve: (value: TenantInfo | PromiseLike<TenantInfo>) => void): void {
    if (isOnline()) {
        $.get(CONFIG.BASE_URL + TENANTS, { domainAlias: HOSTNAME, skipInterceptor: true }, (data) => {
            resolve(data);
        }).fail(function () {
            resolve({ name: getTenantNameFromHostname() });
        });
    } else { // offline
        resolve({ name: getTenantNameFromHostname() });
    }
}

function appendFavicon(rel: string): void {
    const el = document.createElement('link');
    el.setAttribute('href', '/favicon.ico?v=' + new Date().getTime() + '&tenant=' + HOSTNAME);
    el.setAttribute('rel', rel);
    el.setAttribute('type', 'image/x-icon');
    document.getElementsByTagName('head')[0].appendChild(el);
}

function appendFakeFavicon(): void {
    const el = document.createElement('link');
    el.setAttribute('href', 'data:,');
    el.setAttribute('rel', 'icon');
    document.getElementsByTagName('head')[0].appendChild(el);
}

function loadModule(urlPath: string, checksum: string, variableName: string): Promise<any> {

    const MODULE_KEY_PREFIX = 'servitlyScriptContent_';

    if (isOnline()) {
        return new Promise((resolve) => {
            $.ajax(API_URL + urlPath + "?tenant=" + HOSTNAME + "&v=" + checksum, {
                dataType: "text",
                success: (resourceContent: any) => {
                    saveContentToLocalScript(resourceContent, resolve);
                    localStorage.setItem(MODULE_KEY_PREFIX + variableName, resourceContent);
                },
                error: (err) => {
                    console.debug(`Unable to load ${urlPath}`);
                    console.error(err);
                    resolve({});
                }
            });
        });
    } else { // offline
        return new Promise((resolve) => {
            const resourceContent = localStorage.getItem(MODULE_KEY_PREFIX + variableName);
            if (resourceContent) {
                console.log(`Retrieved ${variableName} from local storage`)
                saveContentToLocalScript(resourceContent, resolve);
            } else {
                resolve({});
            }
        })
    }

    function saveContentToLocalScript(resourceContent: any, resolve: (value: any) => void) {
        let f = new Blob([wrapFunctionContent(resourceContent, variableName)], { type: 'application/javascript' });
        let src = URL.createObjectURL(f);
        var $script = require("scriptjs");
        $script(src, function () {
            if (variableName == "filtersAndComponentsExports") {
                resolve(filtersAndComponentsExports);
            } else { // mobileExports
                resolve(mobileExports);
            }
        });
    }

    function wrapFunctionContent(content: string, variableName: string) {
        return "var " + variableName + " = {};" + content.replace(/exports/g, variableName) + ";";
    }
}

function loadModulesChecksum(tenantName: string): Promise<any> {
    if (isOnline()) {
        const customHeaders = {};
        customHeaders[X_SEMIOTY_TENANT] = tenantName;
        return new Promise((resolve, reject) => {
            $.ajax(API_URL + MODULES_SUMMARY, {
                headers: customHeaders,
                dataType: "json",
                success: (modulesSummaryChecksum: object, status: string) => {
                    resolve(modulesSummaryChecksum["checksum"]);
                },
                error: (err, status: string) => {
                    console.debug(`Unable to load summary for tenant ${tenantName}`);
                    console.error(err);
                    resolve({});
                }
            });
        });
    } else { // offline
        return Promise.resolve();
    }
}

export let CUSTOM_FILTERS_AND_COMPONENTS_MAP = {};
export let CUSTOM_MOBILE_MAP = {};
export let API_URL = CONFIG.BASE_URL;
export let LOCALLY_HOSTED = false;
export let HOSTNAME = getHostName();

function getHostName() {
    const queryParamString = window.location.search?.substring(1);
    const HOSTNAME_KEY = 'servitlyHostname';
    if (queryParamString) {
        const queryParams = queryParamString.split('&');
        const tenantUrl = queryParams.find(q => q.startsWith('tenantUrl='));
        if (tenantUrl) {
            const hostname = tenantUrl.split('=')[1];
            localStorage.setItem(HOSTNAME_KEY, hostname);
            LOCALLY_HOSTED = true;
            return hostname;
        }
    } else if (window.location.hostname == 'localhost' || window.location.hostname == '127.0.0.1') {
        LOCALLY_HOSTED = true;
        return localStorage.getItem(HOSTNAME_KEY);
    }
    return window.location.hostname;
}

const tenantInfo$ = getServitlyTenantInfo();

export function setup(): Promise<void> {

    // Set favicon
    if (!isMobile() && !isLocal()) {
        appendFavicon('icon');
        appendFavicon('shortcut icon');
    } else {
        appendFakeFavicon();
    }

    return tenantInfo$.then(tenantInfo => {

        setExternallyAccessibleApiUrl();
        return loadModulesChecksum(tenantInfo.name).then(checksum => {

            // Load filters and mobile modules
            return Promise.all([
                loadModule(CUSTOM_SCRIPTS, checksum, 'filtersAndComponentsExports'),
                isMobile() ? loadModule(CUSTOM_MOBILE, checksum, 'mobileExports') : Promise.resolve({})
            ]).then(result => {
                CUSTOM_FILTERS_AND_COMPONENTS_MAP = result[0];
                CUSTOM_MOBILE_MAP = result[1];
            });
        }).catch(err => console.error(err));
    });
}

function setExternallyAccessibleApiUrl() {
    var baseUrlStrings = API_URL.split(':');
    var lastString = baseUrlStrings[baseUrlStrings.length - 1];
    var apiSuffix = '/api';
    if (baseUrlStrings.length == 3 && baseUrlStrings[2] == '8080') {
        baseUrlStrings[1] = baseUrlStrings[1] + ':' + baseUrlStrings[2];
    }
    window['apiBaseUrl'] = baseUrlStrings[0] + ':' + baseUrlStrings[1] + (lastString.endsWith(apiSuffix) ? apiSuffix : '');
}